import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FileText, Upload, Layers, Image as ImageIcon, FileType, Download, Eye, Trash2, Move, CheckCircle, AlertCircle, Loader, RotateCw, RefreshCw, ChevronUp, ChevronDown, X, FolderOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PDFViewer from '../components/PDFViewer';
import OfficeViewer from '../components/OfficeViewer';
import * as pdfjsLib from 'pdfjs-dist';

// Set worker path for pdf.js - use local public folder file
if (typeof window !== 'undefined') {
  // Use worker file from public folder for reliable loading in all environments
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
}

const PDFTool = () => {
  const [activeTab, setActiveTab] = useState('rearrange');
  const [loading, setLoading] = useState(false);
  const [loadingThumbnails, setLoadingThumbnails] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { user, token, isAdmin, canAccessManager } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Re-arrange state
  const [rearrangeFile, setRearrangeFile] = useState(null);
  const [rearrangePages, setRearrangePages] = useState([]);
  const [rearrangePageOrder, setRearrangePageOrder] = useState([]);
  const [rearrangeOriginalOrder, setRearrangeOriginalOrder] = useState([]);
  const [rearrangeThumbnails, setRearrangeThumbnails] = useState([]);
  const [rearrangeResult, setRearrangeResult] = useState(null);
  const [rearrangeRotations, setRearrangeRotations] = useState({}); // Rotation angles for rearrange pages
  const [previewModal, setPreviewModal] = useState({ show: false, pageNum: null, thumbnail: null, loading: false });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [downloadModal, setDownloadModal] = useState({ show: false, result: null });
  const [pdfViewerModal, setPdfViewerModal] = useState({ show: false, url: null, filename: null });
  const [addPageNumbers, setAddPageNumbers] = useState(false);
  const [removePageNumbers, setRemovePageNumbers] = useState(false); // Remove page numbers from footer
  const [pageNumberLocation, setPageNumberLocation] = useState('center'); // Location: center, left, right


  // Merge state
  const [mergePdf1, setMergePdf1] = useState({ file: null, pages: [], thumbnails: [], pageOrder: [], rotations: {} });
  const [mergePdf2, setMergePdf2] = useState({ file: null, pages: [], thumbnails: [], pageOrder: [], rotations: {} });
  const [mergePdf1Rotations, setMergePdf1Rotations] = useState({}); // Rotation angles for PDF 1 pages
  const [mergePdf2Rotations, setMergePdf2Rotations] = useState({}); // Rotation angles for PDF 2 pages
  const [mergeResult, setMergeResult] = useState(null);
  const [loadingMergeThumbnails, setLoadingMergeThumbnails] = useState(false);
  const [mergedDocPosition, setMergedDocPosition] = useState(null);
  const [mergedCustomOrder, setMergedCustomOrder] = useState(null); // Custom order after manual rearrangement
  const [mergedPageRotations, setMergedPageRotations] = useState({}); // Rotation angles for merged pages
  const [draggingPdfDoc, setDraggingPdfDoc] = useState(null);
  const [draggedMergePageIndex, setDraggedMergePageIndex] = useState(null);
  const [draggedMergedPageIndex, setDraggedMergedPageIndex] = useState(null); // For dragging in merged view
  const [addMergedPageNumbers, setAddMergedPageNumbers] = useState(false); // Add page numbers to merged PDF
  const [removeMergedPageNumbers, setRemoveMergedPageNumbers] = useState(false); // Remove page numbers from footer
  const [mergedPageNumberLocation, setMergedPageNumberLocation] = useState('center'); // Location: center, left, right


  // Image to PDF state
  const [imageFiles, setImageFiles] = useState([]);
  const [imageThumbnails, setImageThumbnails] = useState([]);
  const [imageResult, setImageResult] = useState(null);
  const [enhanceImages, setEnhanceImages] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, index: null, url: null });

  // DOC to PDF state
  const [docFiles, setDocFiles] = useState([]); // Changed from docFile to docFiles array
  const [docResult, setDocResult] = useState(null);
  const [docPreviewModal, setDocPreviewModal] = useState({ show: false, fileUrl: null, fileName: null });

  // Add/Remove Page Numbers state
  const [pageNumFile, setPageNumFile] = useState(null);
  const [pageNumPages, setPageNumPages] = useState([]);
  const [pageNumThumbnails, setPageNumThumbnails] = useState([]);
  const [pageNumResult, setPageNumResult] = useState(null);
  const [pageNumAddNumbers, setPageNumAddNumbers] = useState(false);
  const [pageNumRemoveNumbers, setPageNumRemoveNumbers] = useState(false);
  const [pageNumArea, setPageNumArea] = useState('footer'); // 'header' or 'footer'

  // Session state - Track all uploaded and generated PDFs
  const [sessionGeneratedPDFs, setSessionGeneratedPDFs] = useState([]);
  const [sessionUploadedPDFs, setSessionUploadedPDFs] = useState([]);
  const [sessionPreviewModal, setSessionPreviewModal] = useState({ show: false, pdf: null });
  const [sessionBrowserModal, setSessionBrowserModal] = useState({ show: false, targetTab: null }); // For selecting from session
  
  // Session timer state
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(10 * 60); // 10 minutes in seconds (default)
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [sessionExtendModal, setSessionExtendModal] = useState(false);
  const [sessionExtendTimeout, setSessionExtendTimeout] = useState(null);
  
  // CMS Settings for session management
  const [cmsSettings, setCmsSettings] = useState({
    enable_session_time: true,
    initial_session_time: 10,
    extended_session_time: 10
  });
  
  const [pageNumLocation, setPageNumLocation] = useState('center');
  // Remove page numbers options
  const [pageNumRemoveFormat, setPageNumRemoveFormat] = useState('auto'); // Format to detect and remove
  const [pageNumRemoveFontWeight, setPageNumRemoveFontWeight] = useState('auto'); // Font weight to detect
  // Add page numbers options
  const [pageNumAddArea, setPageNumAddArea] = useState('footer');
  const [pageNumAddLocation, setPageNumAddLocation] = useState('center');
  const [pageNumFormat, setPageNumFormat] = useState('number'); // 'number', 'page-number', 'page-of-total', 'fraction', 'roman-lower', 'roman-upper'
  const [pageNumFontWeight, setPageNumFontWeight] = useState('normal'); // 'normal' or 'bold'
  const [pageNumSelectedPages, setPageNumSelectedPages] = useState([]); // Pages to add numbers to

  const tabs = [
    { id: 'rearrange', label: 'Re-Arrange Pages', icon: Move },
    { id: 'merge', label: 'Merge PDFs', icon: Layers },
    { id: 'pagenumbers', label: 'Add/Remove Page Numbers', icon: FileText },
    { id: 'image', label: 'Image to PDF', icon: ImageIcon },
    { id: 'doc', label: 'DOC to PDF', icon: FileType },
    { id: 'session', label: 'Session', icon: FolderOpen }
  ];

  // Cleanup temp files on unmount
  useEffect(() => {
    return () => {
      // Cleanup will happen automatically as files are stored in temp
      setRearrangeFile(null);
      setMergePdf1({ file: null, pages: [], thumbnails: [], pageOrder: [] });
      setMergePdf2({ file: null, pages: [], thumbnails: [], pageOrder: [] });
      setImageFiles([]);
      setDocFiles([]);
    };
  }, []);

  // Fetch CMS Settings for session management on component mount
  useEffect(() => {
    const fetchCMSSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cms-settings/public`);
        if (response.ok) {
          const data = await response.json();
          setCmsSettings({
            enable_session_time: data.enable_session_time !== undefined ? data.enable_session_time : true,
            initial_session_time: data.initial_session_time || 10,
            extended_session_time: data.extended_session_time || 10
          });
          // Update session duration based on CMS settings
          setSessionDuration((data.initial_session_time || 10) * 60);
        }
      } catch (error) {
        console.error('Error fetching CMS settings:', error);
        // Use defaults on error
      }
    };
    
    fetchCMSSettings();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Start session timer when first file is uploaded
  const startSessionTimer = () => {
    // Check if session management is enabled in CMS settings
    if (!cmsSettings.enable_session_time) {
      return; // Don't start timer if session management is disabled
    }
    
    if (!sessionStartTime) {
      setSessionStartTime(Date.now());
      setSessionTimeLeft(sessionDuration);
    }
  };

  // Extend session by configured extended time
  const extendSession = () => {
    if (sessionExtendTimeout) {
      clearTimeout(sessionExtendTimeout);
      setSessionExtendTimeout(null);
    }
    setSessionExtendModal(false);
    setSessionStartTime(Date.now());
    const extendedTime = cmsSettings.extended_session_time * 60;
    setSessionTimeLeft(extendedTime);
    setSessionDuration(extendedTime);
    showMessage('success', `Session extended by ${cmsSettings.extended_session_time} minutes`);
  };

  // Clear session and refresh page
  const clearSession = () => {
    setSessionGeneratedPDFs([]);
    setSessionUploadedPDFs([]);
    setSessionStartTime(null);
    setSessionTimeLeft(null);
    setSessionExtendModal(false);
    if (sessionExtendTimeout) {
      clearTimeout(sessionExtendTimeout);
      setSessionExtendTimeout(null);
    }
    window.location.reload();
  };

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds === null) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Session timer countdown effect
  useEffect(() => {
    if (!sessionStartTime) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
      const remaining = sessionDuration - elapsed;

      if (remaining <= 0) {
        // Time's up - show modal
        setSessionExtendModal(true);
        setSessionTimeLeft(0);
        
        // Auto-refresh after 30 seconds if no action
        const timeout = setTimeout(() => {
          clearSession();
        }, 30000);
        setSessionExtendTimeout(timeout);
        
        clearInterval(interval);
      } else {
        setSessionTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionStartTime, sessionDuration]);

  // Add uploaded PDF to session
  const addToSessionUploaded = async (file) => {
    try {
      // Start session timer on first upload
      startSessionTimer();
      
      // Check if this filename already exists in generated PDFs (to avoid duplicates)
      const existsInGenerated = sessionGeneratedPDFs.some(pdf => pdf.filename === file.name);
      if (existsInGenerated) {
        console.log('PDF already exists in generated section, skipping upload section');
        return;
      }

      // Generate thumbnail
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1); // First page
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
      const thumbnail = canvas.toDataURL();

      const newPdf = {
        filename: file.name,
        thumbnail,
        file: file,
        timestamp: Date.now(),
        type: 'uploaded'
      };

      // Replace if same filename exists
      setSessionUploadedPDFs(prev => {
        const filtered = prev.filter(p => p.filename !== file.name);
        return [newPdf, ...filtered];
      });
    } catch (error) {
      console.error('Error adding to session:', error);
    }
  };

  // Add generated PDF to session
  const addToSessionGenerated = async (filename, pdfBase64) => {
    try {
      // Convert base64 to thumbnail
      const pdfData = atob(pdfBase64);
      const pdfArray = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        pdfArray[i] = pdfData.charCodeAt(i);
      }
      
      const pdf = await pdfjsLib.getDocument({ data: pdfArray }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
      const thumbnail = canvas.toDataURL();

      const newPdf = {
        filename,
        thumbnail,
        pdfBase64,
        timestamp: Date.now(),
        type: 'generated'
      };

      // Replace if same filename exists
      setSessionGeneratedPDFs(prev => {
        const filtered = prev.filter(p => p.filename !== filename);
        return [newPdf, ...filtered];
      });
    } catch (error) {
      console.error('Error adding generated PDF to session:', error);
    }
  };

  // Handle selecting PDF from session browser
  const handleSelectFromSession = async (pdf, targetTab) => {
    try {
      let file;
      
      // Convert to File object
      if (pdf.type === 'uploaded') {
        file = pdf.file;
      } else {
        // Convert base64 to File
        const base64Data = pdf.pdfBase64;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        file = new File([blob], pdf.filename, { type: 'application/pdf' });
      }
      
      // Load into the appropriate tab
      if (targetTab === 'rearrange') {
        // Simulate file select for rearrange
        const event = { target: { files: [file] } };
        await handleRearrangeFileSelect(event);
      } else if (targetTab === 'pagenumbers') {
        // Simulate file select for page numbers
        const event = { target: { files: [file] } };
        await handlePageNumFileSelect(event);
      } else if (targetTab === 'merge1' || targetTab === 'merge2') {
        // Simulate file select for merge
        const pdfNumber = targetTab === 'merge1' ? 1 : 2;
        const event = { target: { files: [file] } };
        await handleMergeFilesSelect(event, pdfNumber);
      }
      
      showMessage('success', `Loaded ${pdf.filename} from session`);
    } catch (error) {
      console.error('Error loading from session:', error);
      showMessage('error', 'Failed to load PDF from session');
    }
  };

  // Generate high-resolution preview for a specific page
  const generateHighResPreview = async (pageNumber) => {
    if (!rearrangeFile) return null;
    
    try {
      const arrayBuffer = await rearrangeFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(pageNumber);
      
      // Use scale 2.0 for high resolution preview (vs 0.5 for thumbnails)
      const viewport = page.getViewport({ scale: 2.0 });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (err) {
      console.error('Error generating high-res preview:', err);
      return null;
    }
  };

  // Open preview modal with high-res page
  const openPreviewModal = async (pageNum, thumbnailIndex) => {
    // Show modal immediately with thumbnail as placeholder
    setPreviewModal({ 
      show: true, 
      pageNum: pageNum, 
      thumbnail: rearrangeThumbnails[thumbnailIndex],
      loading: true
    });
    
    // Generate high-res version
    const highResImage = await generateHighResPreview(pageNum);
    
    // Update modal with high-res image
    setPreviewModal({ 
      show: true, 
      pageNum: pageNum, 
      thumbnail: highResImage || rearrangeThumbnails[thumbnailIndex],
      loading: false
    });
  };

  // Re-arrange functions
  const handleRearrangeFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      showMessage('error', 'File size must be less than 25MB');
      return;
    }

    if (!file.type.includes('pdf')) {
      showMessage('error', 'Please select a PDF file');
      return;
    }

    setRearrangeFile(file);
    setRearrangeResult(null);
    setLoadingThumbnails(true);

    // Add to session
    await addToSessionUploaded(file);

    try {
      // Load PDF and generate thumbnails
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;
      
      const pages = Array.from({ length: pageCount }, (_, i) => ({
        number: i + 1,
        originalIndex: i
      }));
      
      setRearrangePages(pages);
      const initialOrder = pages.map(p => p.number);
      setRearrangePageOrder(initialOrder);
      setRearrangeOriginalOrder(initialOrder); // Store original order

      // Generate thumbnails for each page
      const thumbnails = [];
      for (let i = 1; i <= pageCount; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        thumbnails.push(thumbnailDataUrl);
      }
      
      setRearrangeThumbnails(thumbnails);
      showMessage('success', `PDF loaded successfully with ${pageCount} pages`);
    } catch (err) {
      console.error('Error processing PDF:', err);
      showMessage('error', 'Error reading PDF file. Please try another file.');
      setRearrangeFile(null);
      setRearrangePages([]);
      setRearrangeThumbnails([]);
    } finally {
      setLoadingThumbnails(false);
    }
  };

  const movePageUp = (index) => {
    if (index === 0) return;
    const newOrder = [...rearrangePageOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setRearrangePageOrder(newOrder);
  };

  const movePageDown = (index) => {
    if (index === rearrangePageOrder.length - 1) return;
    const newOrder = [...rearrangePageOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setRearrangePageOrder(newOrder);
  };

  const removePage = (index) => {
    const newOrder = rearrangePageOrder.filter((_, i) => i !== index);
    setRearrangePageOrder(newOrder);
  };

  // Drag and drop functions
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newOrder = [...rearrangePageOrder];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setRearrangePageOrder(newOrder);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Check if pages have been rearranged
  const isPagesRearranged = () => {
    if (rearrangePageOrder.length !== rearrangeOriginalOrder.length) return true;
    return rearrangePageOrder.some((page, index) => page !== rearrangeOriginalOrder[index]);
  };

  // Reset pages to original order
  const handleResetPages = () => {
    setRearrangePageOrder([...rearrangeOriginalOrder]);
    showMessage('success', 'Pages reset to original order');
  };

  const handleRearrangePDF = async () => {
    if (!rearrangeFile || rearrangePageOrder.length === 0) {
      showMessage('error', 'Please select a PDF file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', rearrangeFile);
      formData.append('page_order', rearrangePageOrder.join(','));
      
      console.log('🔵 SENDING PAGE ORDER:', rearrangePageOrder);

      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/rearrange`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setRearrangeResult(data);
        // Add to session
        await addToSessionGenerated(data.filename, data.pdf_base64);
        // Show download modal
        setDownloadModal({ show: true, result: data });
      } else {
        throw new Error(data.detail || 'Failed to rearrange PDF');
      }
    } catch (err) {
      showMessage('error', err.message || 'Error rearranging PDF');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (result, defaultName = 'output.pdf') => {
    const byteCharacters = atob(result.pdf_base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || defaultName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const previewPDF = (result) => {
    const byteCharacters = atob(result.pdf_base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Open in modal instead of new tab
    setPdfViewerModal({ 
      show: true, 
      url: url, 
      filename: result.filename || 'preview.pdf' 
    });
  };

  // Merge functions
  const handleMergeFilesSelect = async (e, pdfNumber) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      showMessage('error', 'File size must be less than 25MB');
      return;
    }

    if (!file.type.includes('pdf')) {
      showMessage('error', 'Please select a PDF file');
      return;
    }

    setLoadingMergeThumbnails(true);
    setMergeResult(null);

    // Add to session
    await addToSessionUploaded(file);

    try {
      // Load PDF and generate thumbnails
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;
      
      const pages = Array.from({ length: pageCount }, (_, i) => ({
        number: i + 1,
        originalIndex: i
      }));
      
      const initialOrder = pages.map(p => p.number);

      // Generate high-res thumbnails (same as Re-Arrange tab)
      const thumbnails = [];
      for (let i = 1; i <= pageCount; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 }); // High quality like Re-Arrange
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        thumbnails.push(thumbnailDataUrl);
      }
      
      if (pdfNumber === 1) {
        setMergePdf1({ file, pages, thumbnails, pageOrder: initialOrder });
      } else {
        setMergePdf2({ file, pages, thumbnails, pageOrder: initialOrder });
      }
      
      showMessage('success', `PDF ${pdfNumber} loaded with ${pageCount} pages`);
    } catch (err) {
      console.error('Error processing PDF:', err);
      showMessage('error', 'Error reading PDF file');
    } finally {
      setLoadingMergeThumbnails(false);
    }
  };

  const removeMergePage = (pdfNumber, pageIndex) => {
    if (pdfNumber === 1) {
      const newOrder = mergePdf1.pageOrder.filter((_, i) => i !== pageIndex);
      setMergePdf1({ ...mergePdf1, pageOrder: newOrder });
    } else {
      const newOrder = mergePdf2.pageOrder.filter((_, i) => i !== pageIndex);
      setMergePdf2({ ...mergePdf2, pageOrder: newOrder });
    }
  };

  const moveMergePageUp = (pdfNumber, index) => {
    if (index === 0) return;
    if (pdfNumber === 1) {
      const newOrder = [...mergePdf1.pageOrder];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      setMergePdf1({ ...mergePdf1, pageOrder: newOrder });
    } else {
      const newOrder = [...mergePdf2.pageOrder];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      setMergePdf2({ ...mergePdf2, pageOrder: newOrder });
    }
  };

  const moveMergePageDown = (pdfNumber, index, totalPages) => {
    if (index === totalPages - 1) return;
    if (pdfNumber === 1) {
      const newOrder = [...mergePdf1.pageOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setMergePdf1({ ...mergePdf1, pageOrder: newOrder });
    } else {
      const newOrder = [...mergePdf2.pageOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setMergePdf2({ ...mergePdf2, pageOrder: newOrder });
    }
  };

  // Handle dragging entire PDF document
  const handlePdfDragStart = (pdfNumber, e) => {
    console.log('🚀 handlePdfDragStart called - PDF Number:', pdfNumber);
    console.log('📋 Event object:', e);
    console.log('📋 DataTransfer available:', !!e?.dataTransfer);
    
    setDraggingPdfDoc(pdfNumber);
    
    if (e && e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', pdfNumber.toString());
      console.log('✅ DataTransfer properties set successfully');
    } else {
      console.error('❌ DataTransfer not available!');
    }
    
    console.log('🎯 draggingPdfDoc state will be set to:', pdfNumber);
  };

  const handlePdfDragEnd = () => {
    console.log('🏁 handlePdfDragEnd called');
    console.log('🔄 Resetting draggingPdfDoc state');
    setDraggingPdfDoc(null);
  };

  const handleDropPdfBetweenPages = (targetPdf, insertIndex) => {
    console.log('📥 handleDropPdfBetweenPages called');
    console.log('🎯 Target PDF:', targetPdf);
    console.log('📍 Insert Index:', insertIndex);
    console.log('🚀 Dragging PDF Doc:', draggingPdfDoc);
    
    if (!draggingPdfDoc) {
      console.warn('⚠️ No PDF being dragged!');
      return;
    }
    
    console.log('✅ Setting merged document position...');
    setMergedDocPosition({
      targetPdf: targetPdf === 1 ? 'pdf1' : 'pdf2',
      sourcePdf: draggingPdfDoc === 1 ? 'pdf1' : 'pdf2',
      insertAt: insertIndex
    });
    setDraggingPdfDoc(null);
    console.log('✅ Merge completed successfully!');
  };

  // Handle dragging individual pages in merge
  const handleMergePageDragStart = (e, pdfNumber, index) => {
    setDraggedMergePageIndex({ pdf: pdfNumber, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleMergePageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMergePageDrop = (e, pdfNumber, dropIndex) => {
    e.preventDefault();
    
    if (!draggedMergePageIndex || draggedMergePageIndex.pdf !== pdfNumber || draggedMergePageIndex.index === dropIndex) {
      setDraggedMergePageIndex(null);
      return;
    }

    const pdf = pdfNumber === 1 ? mergePdf1 : mergePdf2;
    const newOrder = [...pdf.pageOrder];
    const draggedItem = newOrder[draggedMergePageIndex.index];
    
    // Remove dragged item
    newOrder.splice(draggedMergePageIndex.index, 1);
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    if (pdfNumber === 1) {
      setMergePdf1({ ...mergePdf1, pageOrder: newOrder });
    } else {
      setMergePdf2({ ...mergePdf2, pageOrder: newOrder });
    }
    
    setDraggedMergePageIndex(null);
  };

  const handleMergePageDragEnd = () => {
    setDraggedMergePageIndex(null);
  };

  // Open preview modal for merge pages
  const openMergePreviewModal = async (pdfNumber, pageNum, thumbnailIndex) => {
    const pdf = pdfNumber === 1 ? mergePdf1 : mergePdf2;
    
    // Show modal immediately with thumbnail as placeholder
    setPreviewModal({ 
      show: true, 
      pageNum: pageNum, 
      thumbnail: pdf.thumbnails[thumbnailIndex],
      loading: true
    });
    
    // Generate high-res version
    const highResImage = await generateHighResMergePreview(pdfNumber, pageNum);
    
    // Update modal with high-res image
    setPreviewModal({ 
      show: true, 
      pageNum: pageNum, 
      thumbnail: highResImage || pdf.thumbnails[thumbnailIndex],
      loading: false
    });
  };

  // Generate high-resolution preview for merge tab
  const generateHighResMergePreview = async (pdfNumber, pageNumber) => {
    const pdf = pdfNumber === 1 ? mergePdf1 : mergePdf2;
    if (!pdf.file) return null;
    
    try {
      const arrayBuffer = await pdf.file.arrayBuffer();
      const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdfDoc.getPage(pageNumber);
      
      const viewport = page.getViewport({ scale: 2.0 });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (err) {
      console.error('Error generating high-res preview:', err);
      return null;
    }
  };

  const removeMergedDoc = () => {
    setMergedDocPosition(null);
    setMergedCustomOrder(null);
  };

  const moveMergedDocUp = () => {
    if (!mergedDocPosition || mergedDocPosition.insertAt === 0) return;
    setMergedDocPosition({
      ...mergedDocPosition,
      insertAt: mergedDocPosition.insertAt - 1
    });
  };

  const moveMergedDocDown = (maxPosition) => {
    if (!mergedDocPosition || mergedDocPosition.insertAt >= maxPosition) return;
    setMergedDocPosition({
      ...mergedDocPosition,
      insertAt: mergedDocPosition.insertAt + 1
    });
  };

  // Handle dragging pages in merged view
  const handleMergedPageDragStart = (e, index) => {
    setDraggedMergedPageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleMergedPageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMergedPageDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedMergedPageIndex === null || draggedMergedPageIndex === dropIndex) {
      setDraggedMergedPageIndex(null);
      return;
    }

    // Get current merged order
    const currentOrder = getMergedPageOrder();
    const newOrder = [...currentOrder];
    const draggedItem = newOrder[draggedMergedPageIndex];
    
    // Remove dragged item
    newOrder.splice(draggedMergedPageIndex, 1);
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setMergedCustomOrder(newOrder);
    setDraggedMergedPageIndex(null);
  };

  const handleMergedPageDragEnd = () => {
    setDraggedMergedPageIndex(null);
  };

  // Remove a page from merged view
  const removeMergedPage = (index) => {
    const currentOrder = getMergedPageOrder();
    if (currentOrder.length <= 1) {
      showMessage('error', 'Cannot remove the last page');
      return;
    }
    const newOrder = currentOrder.filter((_, i) => i !== index);
    setMergedCustomOrder(newOrder);
  };

  // Open preview modal for merged view pages
  const openMergedPagePreview = async (item, thumbnailIndex) => {
    const pdf = item.pdf === 'pdf1' || item.pdf === 1 ? mergePdf1 : mergePdf2;
    
    // Show modal immediately with thumbnail as placeholder
    setPreviewModal({ 
      show: true, 
      pageNum: item.page, 
      thumbnail: pdf.thumbnails[thumbnailIndex],
      loading: true
    });
    
    // Generate high-res version
    const pdfNumber = item.pdf === 'pdf1' || item.pdf === 1 ? 1 : 2;
    const highResImage = await generateHighResMergePreview(pdfNumber, item.page);
    
    // Update modal with high-res image
    setPreviewModal({ 
      show: true, 
      pageNum: item.page, 
      thumbnail: highResImage || pdf.thumbnails[thumbnailIndex],
      loading: false
    });
  };

  // Rotate a page in merged view
  const rotateMergedPage = (index) => {
    const currentRotation = mergedPageRotations[index] || 0;
    const newRotation = (currentRotation + 90) % 360;
    setMergedPageRotations({
      ...mergedPageRotations,
      [index]: newRotation
    });
  };

  // Move merged page up
  const moveMergedPageUp = (index) => {
    if (index === 0) return;
    const currentOrder = getMergedPageOrder();
    const newOrder = [...currentOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setMergedCustomOrder(newOrder);
    
    // Swap rotations too
    const newRotations = { ...mergedPageRotations };
    const tempRotation = newRotations[index];
    newRotations[index] = newRotations[index - 1] || 0;
    newRotations[index - 1] = tempRotation || 0;
    setMergedPageRotations(newRotations);
  };

  // Move merged page down
  const moveMergedPageDown = (index) => {
    const currentOrder = getMergedPageOrder();
    if (index === currentOrder.length - 1) return;
    const newOrder = [...currentOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setMergedCustomOrder(newOrder);
    
    // Swap rotations too
    const newRotations = { ...mergedPageRotations };
    const tempRotation = newRotations[index];
    newRotations[index] = newRotations[index + 1] || 0;
    newRotations[index + 1] = tempRotation || 0;
    setMergedPageRotations(newRotations);
  };

  // Rotate a page in rearrange tab
  const rotateRearrangePage = (index) => {
    const currentRotation = rearrangeRotations[index] || 0;
    const newRotation = (currentRotation + 90) % 360;
    setRearrangeRotations({
      ...rearrangeRotations,
      [index]: newRotation
    });
  };

  // Rotate a page in unmerged PDF 1
  const rotateMergePdf1Page = (index) => {
    const currentRotation = mergePdf1Rotations[index] || 0;
    const newRotation = (currentRotation + 90) % 360;
    setMergePdf1Rotations({
      ...mergePdf1Rotations,
      [index]: newRotation
    });
  };

  // Rotate a page in unmerged PDF 2
  const rotateMergePdf2Page = (index) => {
    const currentRotation = mergePdf2Rotations[index] || 0;
    const newRotation = (currentRotation + 90) % 360;
    setMergePdf2Rotations({
      ...mergePdf2Rotations,
      [index]: newRotation
    });
  };

  // Reset merge state
  const resetMerge = () => {
    setMergePdf1({ file: null, pages: [], thumbnails: [], pageOrder: [], rotations: {} });
    setMergePdf2({ file: null, pages: [], thumbnails: [], pageOrder: [], rotations: {} });
    setMergePdf1Rotations({});
    setMergePdf2Rotations({});
    setMergeResult(null);
    setMergedDocPosition(null);
    setMergedCustomOrder(null);
    setMergedPageRotations({});
    setDraggingPdfDoc(null);
    setDraggedMergePageIndex(null);
    setDraggedMergedPageIndex(null);
    showMessage('success', 'Merge state reset');
  };

  // Get the current merged page order (either custom or default)
  const getMergedPageOrder = () => {
    if (mergedCustomOrder) {
      return mergedCustomOrder;
    }
    
    if (!mergedDocPosition) return [];
    
    const targetPdf = mergedDocPosition.targetPdf === 'pdf1' ? mergePdf1 : mergePdf2;
    const sourcePdf = mergedDocPosition.sourcePdf === 'pdf1' ? mergePdf1 : mergePdf2;
    
    return [
      ...targetPdf.pageOrder.slice(0, mergedDocPosition.insertAt).map(p => ({ 
        pdf: mergedDocPosition.targetPdf, 
        page: p 
      })),
      ...sourcePdf.pageOrder.map(p => ({ 
        pdf: mergedDocPosition.sourcePdf, 
        page: p 
      })),
      ...targetPdf.pageOrder.slice(mergedDocPosition.insertAt).map(p => ({ 
        pdf: mergedDocPosition.targetPdf, 
        page: p 
      }))
    ];
  };

  const handleMergePDFs = async () => {
    if (!mergePdf1.file || !mergePdf2.file) {
      showMessage('error', 'Please select both PDF files');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      
      // Use custom order if available, otherwise use default merged order
      let finalOrder = [];
      if (mergedCustomOrder) {
        // User has manually rearranged pages in merged view
        finalOrder = mergedCustomOrder;
      } else if (!mergedDocPosition) {
        // No merge, just concat pdf1 + pdf2
        finalOrder = [
          ...mergePdf1.pageOrder.map(p => ({ pdf: 1, page: p })),
          ...mergePdf2.pageOrder.map(p => ({ pdf: 2, page: p }))
        ];
      } else {
        // Use merged position
        finalOrder = getMergedPageOrder();
      }
      
      // Create page order string: "1:3,1:1,1:2,2:1,2:2" (pdf:page format)
      const pageOrderStr = finalOrder.map(item => {
        const pdfNum = item.pdf === 'pdf1' ? 1 : (item.pdf === 'pdf2' ? 2 : item.pdf);
        return `${pdfNum}:${item.page}`;
      }).join(',');
      
      formData.append('file1', mergePdf1.file);
      formData.append('file2', mergePdf2.file);
      formData.append('page_order', pageOrderStr);

      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/merge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setMergeResult(data);
        // Add to session
        await addToSessionGenerated(data.filename, data.pdf_base64);
        // Show download modal (same as Re-Arrange tab)
        setDownloadModal({ show: true, result: data });
      } else {
        throw new Error(data.detail || 'Failed to merge PDFs');
      }
    } catch (err) {
      showMessage('error', err.message || 'Error merging PDFs');
    } finally {
      setLoading(false);
    }
  };


  // Add/Remove Page Numbers functions
  const handlePageNumFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      showMessage('error', 'Please select a PDF file');
      return;
    }
    
    if (file.size > 25 * 1024 * 1024) {
      showMessage('error', 'File size must be less than 25MB');
      return;
    }
    
    setPageNumFile(file);
    setPageNumResult(null);
    setLoadingThumbnails(true);
    
    // Add to session
    await addToSessionUploaded(file);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      const pages = [];
      const thumbnails = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        pages.push(i);
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.0 }); // Increased from 0.3 for better quality
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        thumbnails.push(canvas.toDataURL());
      }
      
      setPageNumPages(pages);
      setPageNumThumbnails(thumbnails);
      // Select all pages by default when file is loaded
      setPageNumSelectedPages(pages);
    } catch (err) {
      console.error('Error loading PDF:', err);
      showMessage('error', 'Failed to load PDF file');
    } finally {
      setLoadingThumbnails(false);
    }
  };
  
  const handleProcessPageNumbers = async () => {
    if (!pageNumFile) {
      showMessage('error', 'Please select a PDF file');
      return;
    }
    
    if (!pageNumAddNumbers && !pageNumRemoveNumbers) {
      showMessage('error', 'Please select at least one operation (Add or Remove page numbers)');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', pageNumFile);
      formData.append('add_page_numbers', pageNumAddNumbers);
      formData.append('remove_page_numbers', pageNumRemoveNumbers);
      formData.append('page_number_area', pageNumArea);
      formData.append('page_number_location', pageNumLocation);
      formData.append('remove_page_number_format', pageNumRemoveFormat);
      formData.append('remove_page_number_font_weight', pageNumRemoveFontWeight);
      formData.append('add_page_number_area', pageNumAddArea);
      formData.append('add_page_number_location', pageNumAddLocation);
      formData.append('page_number_format', pageNumFormat);
      formData.append('page_number_font_weight', pageNumFontWeight);
      formData.append('selected_pages', pageNumSelectedPages.join(',')); // Send as comma-separated string
      
      console.log('🔵 SENDING:', { 
        add_page_numbers: pageNumAddNumbers, 
        remove_page_numbers: pageNumRemoveNumbers,
        page_number_area: pageNumArea,
        page_number_location: pageNumLocation,
        remove_page_number_format: pageNumRemoveFormat,
        remove_page_number_font_weight: pageNumRemoveFontWeight,
        add_page_number_area: pageNumAddArea,
        add_page_number_location: pageNumAddLocation,
        page_number_format: pageNumFormat,
        page_number_font_weight: pageNumFontWeight,
        selected_pages: pageNumSelectedPages
      });

      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/add-remove-page-numbers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to process page numbers');
      }
      
      // Get JSON response with base64 PDF
      const data = await response.json();
      if (data.success) {
        setPageNumResult(data);
        // Add to session
        await addToSessionGenerated(data.filename, data.pdf_base64);
        // Show download modal with preview option
        setDownloadModal({ show: true, result: data });
        showMessage('success', 'PDF processed successfully!');
      } else {
        throw new Error(data.detail || 'Failed to process page numbers');
      }
    } catch (err) {
      console.error('Error:', err);
      showMessage('error', err.message || 'Failed to process page numbers');
    } finally {
      setLoading(false);
    }
  };

  const resetPageNumbers = () => {
    setPageNumFile(null);
    setPageNumPages([]);
    setPageNumThumbnails([]);
    setPageNumResult(null);
    setPageNumAddNumbers(false);
    setPageNumRemoveNumbers(false);
    setPageNumArea('footer');
    setPageNumLocation('center');
    setPageNumRemoveFormat('auto');
    setPageNumRemoveFontWeight('auto');
    setPageNumAddArea('footer');
    setPageNumAddLocation('center');
    setPageNumFormat('number');
    setPageNumFontWeight('normal');
    setPageNumSelectedPages([]);
  };

  const togglePageNumSelection = (pageNum) => {
    setPageNumSelectedPages(prev => {
      if (prev.includes(pageNum)) {
        return prev.filter(p => p !== pageNum);
      } else {
        return [...prev, pageNum];
      }
    });
  };

  const selectAllPageNums = () => {
    setPageNumSelectedPages([...pageNumPages]);
  };

  const deselectAllPageNums = () => {
    setPageNumSelectedPages([]);
  };

  // Image to PDF functions
  const handleImageFilesSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Append to existing files instead of replacing
    const newFiles = [...imageFiles, ...files];
    setImageFiles(newFiles);
    
    // Generate thumbnails for new files
    const newThumbnails = [...imageThumbnails];
    for (let file of files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        newThumbnails.push(event.target.result);
        setImageThumbnails([...newThumbnails]);
      };
      reader.readAsDataURL(file);
    }
  };

  const moveImageUp = (index) => {
    if (index === 0) return;
    const newFiles = [...imageFiles];
    const newThumbnails = [...imageThumbnails];
    [newFiles[index], newFiles[index - 1]] = [newFiles[index - 1], newFiles[index]];
    [newThumbnails[index], newThumbnails[index - 1]] = [newThumbnails[index - 1], newThumbnails[index]];
    setImageFiles(newFiles);
    setImageThumbnails(newThumbnails);
  };

  const moveImageDown = (index) => {
    if (index === imageFiles.length - 1) return;
    const newFiles = [...imageFiles];
    const newThumbnails = [...imageThumbnails];
    [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
    [newThumbnails[index], newThumbnails[index + 1]] = [newThumbnails[index + 1], newThumbnails[index]];
    setImageFiles(newFiles);
    setImageThumbnails(newThumbnails);
  };

  const removeImage = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImageThumbnails(imageThumbnails.filter((_, i) => i !== index));
  };

  const moveImageFileUp = (index) => moveImageUp(index);
  const moveImageFileDown = (index) => moveImageDown(index);
  const removeImageFile = (index) => removeImage(index);

  const handleImageToPDF = async () => {
    if (imageFiles.length === 0) {
      showMessage('error', 'Please select at least 1 image file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      imageFiles.forEach(file => formData.append('files', file));
      formData.append('enhance', enhanceImages);

      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/image-to-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setImageResult(data);
        // Add to session
        await addToSessionGenerated(data.filename, data.pdf_base64);
        // Show download modal with preview option
        setDownloadModal({ show: true, result: data });
        showMessage('success', 'Images converted to PDF successfully!');
      } else {
        throw new Error(data.detail || 'Failed to convert images');
      }
    } catch (err) {
      showMessage('error', err.message || 'Error converting images to PDF');
    } finally {
      setLoading(false);
    }
  };

  // DOC to PDF functions
  const handleDocFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Filter only DOC/DOCX files
    const docOnlyFiles = files.filter(f => 
      f.name.endsWith('.doc') || 
      f.name.endsWith('.docx') || 
      f.type === 'application/msword' || 
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    
    if (docOnlyFiles.length === 0) {
      showMessage('error', 'Please select valid DOC or DOCX files');
      return;
    }
    
    // Append to existing files instead of replacing
    const newFiles = [...docFiles, ...docOnlyFiles];
    setDocFiles(newFiles);
  };

  const moveDocUp = (index) => {
    if (index === 0) return;
    const newFiles = [...docFiles];
    [newFiles[index], newFiles[index - 1]] = [newFiles[index - 1], newFiles[index]];
    setDocFiles(newFiles);
  };

  const moveDocDown = (index) => {
    if (index === docFiles.length - 1) return;
    const newFiles = [...docFiles];
    [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
    setDocFiles(newFiles);
  };

  const removeDoc = (index) => {
    setDocFiles(docFiles.filter((_, i) => i !== index));
  };

  const handleDocPreview = async (file) => {
    try {
      // Upload the file temporarily to backend to get a URL that OfficeViewer can use
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/temp-upload-doc`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Backend error:', errorData);
        showMessage('error', errorData.detail || 'Failed to prepare document for preview');
        return;
      }
      
      const data = await response.json();
      console.log('Preview response:', data);
      
      if (data.success && data.file_url) {
        setDocPreviewModal({
          show: true,
          fileUrl: data.file_url,
          fileName: file.name
        });
      } else {
        console.error('Invalid response structure:', data);
        showMessage('error', 'Failed to prepare document for preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      showMessage('error', 'Failed to preview document: ' + error.message);
    }
  };

  const handleDocToPDF = async () => {
    if (docFiles.length === 0) {
      showMessage('error', 'Please select at least one DOC/DOCX file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      docFiles.forEach(file => formData.append('files', file));

      const response = await fetch(`${API_BASE_URL}/api/pdf-tool/doc-to-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setDocResult(data);
        // Add to session
        await addToSessionGenerated(data.filename, data.pdf_base64);
        // Show download modal with preview option
        setDownloadModal({ show: true, result: data });
        showMessage('success', 'Documents converted to PDF successfully!');
      } else {
        throw new Error(data.detail || 'Failed to convert documents');
      }
    } catch (err) {
      showMessage('error', err.message || 'Error converting documents to PDF');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !canAccessManager()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">Manager or admin privileges required.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                PDF Tools
              </h2>
              
              {/* Session Timer */}
              {sessionTimeLeft !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Session:</span>
                  <div className={`px-3 py-1 rounded-lg font-semibold text-sm ${
                    sessionTimeLeft <= 60 
                      ? 'bg-red-100 text-red-700 animate-pulse' 
                      : sessionTimeLeft <= 180
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    ⏱️ {formatTime(sessionTimeLeft)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mx-6 mt-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
              message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
              'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              <div className="flex items-center">
                {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> :
                 message.type === 'error' ? <AlertCircle className="h-5 w-5 mr-2" /> : null}
                <p className="text-sm">{message.text}</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                
                {/* Re-Arrange Pages Tab */}
                {activeTab === 'rearrange' && (
                  <div className="space-y-6">
                    
                    {/* Upload Section with Generate PDF Button */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        {/* Custom Upload Button */}
                        <div className="flex-1 flex items-center gap-3">
                          <input
                            type="file"
                            id="pdf-upload-input"
                            accept="application/pdf"
                            onChange={handleRearrangeFileSelect}
                            disabled={loadingThumbnails}
                            className="hidden"
                          />
                          <label
                            htmlFor="pdf-upload-input"
                            className={`inline-flex items-center justify-center px-6 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors ${
                              loadingThumbnails 
                                ? 'opacity-50 cursor-not-allowed' 
                                : 'hover:opacity-90'
                            }`}
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF
                          </label>
                          
                          {/* Browse Session Button */}
                          {(sessionGeneratedPDFs.length > 0 || sessionUploadedPDFs.length > 0) && (
                            <button
                              onClick={() => setSessionBrowserModal({ show: true, targetTab: 'rearrange' })}
                              disabled={loadingThumbnails}
                              className="inline-flex items-center justify-center px-4 py-2 bg-white border-2 border-blue-400 text-theme-blue rounded-lg font-medium text-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
                            >
                              <FolderOpen className="h-4 w-4 mr-2" />
                              Browse Session
                            </button>
                          )}
                        </div>
                        
                        {rearrangeFile && !loadingThumbnails && (
                          <div className="flex gap-2">
                            {/* Reset Button */}
                            <button
                              onClick={handleResetPages}
                              disabled={!isPagesRearranged()}
                              className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors whitespace-nowrap"
                              title={!isPagesRearranged() ? 'No changes to reset' : 'Reset to original order'}
                            >
                              Reset
                            </button>
                            
                            {/* Generate PDF Button */}
                            <button
                              onClick={handleRearrangePDF}
                              disabled={loading || !isPagesRearranged()}
                              className="px-6 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              title={!isPagesRearranged() ? 'Rearrange pages to enable' : 'Generate PDF with new page order'}
                            >
                              {loading ? (
                                <span className="flex items-center">
                                  <Loader className="animate-spin h-4 w-4 mr-2" />
                                  Processing...
                                </span>
                              ) : (
                                'Generate PDF'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {loadingThumbnails && (
                      <div className="flex items-center justify-center py-8">
                        <Loader className="animate-spin h-8 w-8 mr-2" style={{ color: 'var(--theme-primary)' }} />
                        <span className="text-sm text-gray-600">Loading PDF and generating thumbnails...</span>
                      </div>
                    )}

                    {rearrangeFile && rearrangePages.length > 0 && !loadingThumbnails && (
                      <div className="border border-gray-300 rounded-lg p-4">
                        {/* Filename Header */}
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-800">
                            {rearrangeFile.name.split('.').pop().toUpperCase()} - {rearrangeFile.name} ({rearrangePageOrder.length} {rearrangePageOrder.length === 1 ? 'page' : 'pages'})
                          </h3>
                          {isPagesRearranged() && (
                            <span className="text-xs text-theme-blue font-medium">Order Modified</span>
                          )}
                        </div>
                        
                        {/* Grid Layout - 5 columns */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                          {rearrangePageOrder.map((pageNum, index) => {
                            const thumbnailIndex = pageNum - 1;
                            const isDragging = draggedIndex === index;
                            
                            return (
                              <div
                                key={index}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                className={`bg-white rounded-lg border-2 p-3 transition-all cursor-move ${
                                  isDragging 
                                    ? 'border-blue-400 opacity-50 scale-95' 
                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                              >
                                {/* Thumbnail */}
                                {rearrangeThumbnails[thumbnailIndex] && (
                                  <div className="mb-3">
                                    <img 
                                      src={rearrangeThumbnails[thumbnailIndex]} 
                                      alt={`Page ${pageNum}`}
                                      className="w-full h-auto object-contain border border-gray-200 rounded transition-transform"
                                      style={{ 
                                        transform: `rotate(${rearrangeRotations[index] || 0}deg)`,
                                        transition: 'transform 0.3s ease'
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Page Info */}
                                <div className="text-center mb-2">
                                  <span className="text-sm font-semibold text-gray-800">Page {pageNum}</span>
                                  <p className="text-xs text-gray-500 mt-0.5">Position: {index + 1}</p>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex items-center justify-center space-x-1">
                                  {/* Preview Button */}
                                  <button
                                    onClick={() => openPreviewModal(pageNum, thumbnailIndex)}
                                    className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Preview page"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  
                                  {/* Rotate Button */}
                                  <button
                                    onClick={() => rotateRearrangePage(index)}
                                    className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                    title="Rotate page 90°"
                                  >
                                    <RotateCw className="h-4 w-4" />
                                  </button>
                                  
                                  {/* Move Up Button */}
                                  <button
                                    onClick={() => movePageUp(index)}
                                    disabled={index === 0}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Move up"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  </button>
                                  
                                  {/* Move Down Button */}
                                  <button
                                    onClick={() => movePageDown(index)}
                                    disabled={index === rearrangePageOrder.length - 1}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Move down"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  
                                  {/* Remove Button */}
                                  <button
                                    onClick={() => removePage(index)}
                                    disabled={rearrangePageOrder.length === 1}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Remove page"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {rearrangeResult && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-800">PDF Ready!</p>
                            <p className="text-xs text-green-600 mt-1">
                              {rearrangeResult.total_pages} pages • {rearrangeResult.filename}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => previewPDF(rearrangeResult)}
                              className="px-3 py-1.5 bg-white text-green-700 border border-green-300 rounded-lg text-sm font-medium hover:bg-green-50 flex items-center"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </button>
                            <button
                              onClick={() => downloadPDF(rearrangeResult)}
                              className="px-3 py-1.5 text-white rounded-lg text-sm font-medium flex items-center"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Merge PDFs Tab */}
                {activeTab === 'merge' && (
                  <div className="space-y-6">
                    
                    {/* Upload Section with Action Buttons - All in One Row */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        {/* PDF 1 Upload */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <input
                            type="file"
                            id="merge-pdf1-input"
                            accept="application/pdf"
                            onChange={(e) => handleMergeFilesSelect(e, 1)}
                            disabled={loadingMergeThumbnails}
                            className="hidden"
                          />
                          <label
                            htmlFor="merge-pdf1-input"
                            className={`inline-flex items-center justify-center px-4 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors ${
                              loadingMergeThumbnails ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                            }`}
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF 1
                          </label>
                          
                          {/* Browse Session for PDF 1 */}
                          {(sessionGeneratedPDFs.length > 0 || sessionUploadedPDFs.length > 0) && (
                            <button
                              onClick={() => setSessionBrowserModal({ show: true, targetTab: 'merge1' })}
                              disabled={loadingMergeThumbnails}
                              className="inline-flex items-center justify-center px-3 py-2 bg-white border-2 border-blue-400 text-theme-blue rounded-lg font-medium text-xs hover:bg-blue-50 transition-colors disabled:opacity-50"
                              title="Browse Session for PDF 1"
                            >
                              <FolderOpen className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        {/* PDF 2 Upload */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <input
                            type="file"
                            id="merge-pdf2-input"
                            accept="application/pdf"
                            onChange={(e) => handleMergeFilesSelect(e, 2)}
                            disabled={loadingMergeThumbnails}
                            className="hidden"
                          />
                          <label
                            htmlFor="merge-pdf2-input"
                            className={`inline-flex items-center justify-center px-4 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors ${
                              loadingMergeThumbnails ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                            }`}
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF 2
                          </label>
                          
                          {/* Browse Session for PDF 2 */}
                          {(sessionGeneratedPDFs.length > 0 || sessionUploadedPDFs.length > 0) && (
                            <button
                              onClick={() => setSessionBrowserModal({ show: true, targetTab: 'merge2' })}
                              disabled={loadingMergeThumbnails}
                              className="inline-flex items-center justify-center px-3 py-2 bg-white border-2 border-blue-400 text-theme-blue rounded-lg font-medium text-xs hover:bg-blue-50 transition-colors disabled:opacity-50"
                              title="Browse Session for PDF 2"
                            >
                              <FolderOpen className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        {/* Reset Button - pushed to right */}
                        <button
                          onClick={resetMerge}
                          disabled={!mergePdf1.file && !mergePdf2.file}
                          className="flex-shrink-0 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium text-sm disabled:opacity-50 hover:bg-gray-300 transition-colors flex items-center"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reset
                        </button>

                        {/* Show Merged Preview Button (before merge) */}
                        {!mergedDocPosition && (
                          <button
                            onClick={() => {
                              // Create merged position showing PDF 1 followed by PDF 2
                              setMergedDocPosition({
                                targetPdf: 'pdf1',
                                sourcePdf: 'pdf2',
                                insertAt: mergePdf1.pageOrder.length // Insert PDF 2 after all PDF 1 pages
                              });
                              showMessage('success', 'PDFs merged! You can now rearrange pages.');
                            }}
                            disabled={!mergePdf1.file || !mergePdf2.file || loadingMergeThumbnails}
                            className="flex-shrink-0 px-4 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 transition-colors flex items-center"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Layers className="h-4 w-4 mr-2" />
                            Merge & Preview
                          </button>
                        )}

                        {/* Generate Merged PDF Button (after merge preview shown) */}
                        {mergedDocPosition && (
                          <button
                            onClick={handleMergePDFs}
                            disabled={loading}
                            className="flex-shrink-0 px-4 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 transition-colors flex items-center"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            {loading ? (
                              <span className="flex items-center">
                                <Loader className="animate-spin h-4 w-4 mr-2" />
                                Generating...
                              </span>
                            ) : (
                              <>
                                <Download className="h-4 w-4 mr-2" />
                                Generate PDF
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                      
                    {/* Helper Text */}
                    {mergePdf1.file && mergePdf2.file && !loadingMergeThumbnails && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-700">
                          📋 <strong>Merge Order:</strong> PDF 1 pages will come first, followed by PDF 2 pages. 
                          You can rearrange, rotate, or delete pages in each PDF before and after merging.
                        </p>
                      </div>
                    )}

                    {loadingMergeThumbnails && (
                      <div className="flex items-center justify-center py-8">
                        <Loader className="animate-spin h-8 w-8 mr-2" style={{ color: 'var(--theme-primary)' }} />
                        <span className="text-sm text-gray-600">Loading PDFs and generating thumbnails...</span>
                      </div>
                    )}

                    {/* PDF 1 - Horizontal Scrollable Row */}
                    {mergePdf1.file && !loadingMergeThumbnails && !mergedDocPosition && (
                      <div className="bg-white rounded-lg p-4 border-2 border-gray-300">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-800">
                            PDF 1 - {mergePdf1.file.name} ({mergePdf1.pageOrder.length} pages)
                          </h3>
                        </div>

                        {/* Horizontal Scrollable Container - 5 pages viewport */}
                        <div 
                          className="overflow-x-auto"
                          onDragStart={(e) => {
                            // Prevent child drags from bubbling up
                            e.stopPropagation();
                          }}
                        >
                          <div className="flex gap-3 pb-2" style={{ minWidth: 'min-content' }}>
                            {mergePdf1.pageOrder.map((pageNum, index) => {
                              const thumbnailIndex = pageNum - 1;
                              const isDragging = draggedMergePageIndex?.pdf === 1 && draggedMergePageIndex?.index === index;
                              
                              return (
                                <div
                                  key={index}
                                  draggable
                                  onDragStart={(e) => handleMergePageDragStart(e, 1, index)}
                                  onDragOver={handleMergePageDragOver}
                                  onDrop={(e) => handleMergePageDrop(e, 1, index)}
                                  onDragEnd={handleMergePageDragEnd}
                                  className={`flex-shrink-0 bg-white rounded-lg border-2 p-3 transition-all cursor-move ${
                                    isDragging 
                                      ? 'border-blue-400 opacity-50 scale-95' 
                                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                                  }`}
                                  style={{ width: '180px' }}
                                >
                                  {/* Thumbnail */}
                                  {mergePdf1.thumbnails[thumbnailIndex] && (
                                    <div className="mb-3">
                                      <img 
                                        src={mergePdf1.thumbnails[thumbnailIndex]} 
                                        alt={`Page ${pageNum}`}
                                        className="w-full h-auto object-contain border border-gray-200 rounded transition-transform"
                                        style={{ 
                                          transform: `rotate(${mergePdf1Rotations[index] || 0}deg)`,
                                          transition: 'transform 0.3s ease'
                                        }}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Page Info */}
                                  <div className="text-center mb-2">
                                    <span className="text-sm font-semibold text-gray-800">Page {pageNum}</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Position: {index + 1}</p>
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex items-center justify-center space-x-1">
                                    {/* Preview Button */}
                                    <button
                                      onClick={() => openMergePreviewModal(1, pageNum, thumbnailIndex)}
                                      className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                      title="Preview page"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Rotate Button */}
                                    <button
                                      onClick={() => rotateMergePdf1Page(index)}
                                      className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                      title="Rotate page 90°"
                                    >
                                      <RotateCw className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Move Up Button */}
                                    <button
                                      onClick={() => moveMergePageUp(1, index)}
                                      disabled={index === 0}
                                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Move up"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                      </svg>
                                    </button>
                                    
                                    {/* Move Down Button */}
                                    <button
                                      onClick={() => moveMergePageDown(1, index, mergePdf1.pageOrder.length)}
                                      disabled={index === mergePdf1.pageOrder.length - 1}
                                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Move down"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                    
                                    {/* Remove Button */}
                                    <button
                                      onClick={() => removeMergePage(1, index)}
                                      disabled={mergePdf1.pageOrder.length === 1}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Remove page"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Drop Zone Between PDFs */}
                    {mergePdf1.file && mergePdf2.file && draggingPdfDoc && !mergedDocPosition && (
                      <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDropPdfBetweenPages(draggingPdfDoc === 1 ? 2 : 1, 0)}
                        className="h-16 border-2 border-dashed border-blue-400 bg-blue-50 rounded-lg flex items-center justify-center text-sm text-theme-blue font-medium hover:bg-blue-100 transition-colors"
                      >
                        Drop PDF {draggingPdfDoc} here to insert at the beginning
                      </div>
                    )}

                    {/* PDF 2 - Horizontal Scrollable Row */}
                    {mergePdf2.file && !loadingMergeThumbnails && !mergedDocPosition && (
                      <div className="bg-white rounded-lg p-4 border-2 border-gray-300">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-800">
                            PDF 2 - {mergePdf2.file.name} ({mergePdf2.pageOrder.length} pages)
                          </h3>
                        </div>

                        {/* Horizontal Scrollable Container - 5 pages viewport */}
                        <div 
                          className="overflow-x-auto"
                          onDragStart={(e) => {
                            // Prevent child drags from bubbling up
                            e.stopPropagation();
                          }}
                        >
                          <div className="flex gap-3 pb-2" style={{ minWidth: 'min-content' }}>
                            {mergePdf2.pageOrder.map((pageNum, index) => {
                              const thumbnailIndex = pageNum - 1;
                              const isDragging = draggedMergePageIndex?.pdf === 2 && draggedMergePageIndex?.index === index;
                              
                              return (
                                <div
                                  key={index}
                                  draggable
                                  onDragStart={(e) => handleMergePageDragStart(e, 2, index)}
                                  onDragOver={handleMergePageDragOver}
                                  onDrop={(e) => handleMergePageDrop(e, 2, index)}
                                  onDragEnd={handleMergePageDragEnd}
                                  className={`flex-shrink-0 bg-white rounded-lg border-2 p-3 transition-all cursor-move ${
                                    isDragging 
                                      ? 'border-blue-400 opacity-50 scale-95' 
                                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                                  }`}
                                  style={{ width: '180px' }}
                                >
                                  {/* Thumbnail */}
                                  {mergePdf2.thumbnails[thumbnailIndex] && (
                                    <div className="mb-3">
                                      <img 
                                        src={mergePdf2.thumbnails[thumbnailIndex]} 
                                        alt={`Page ${pageNum}`}
                                        className="w-full h-auto object-contain border border-gray-200 rounded transition-transform"
                                        style={{ 
                                          transform: `rotate(${mergePdf2Rotations[index] || 0}deg)`,
                                          transition: 'transform 0.3s ease'
                                        }}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Page Info */}
                                  <div className="text-center mb-2">
                                    <span className="text-sm font-semibold text-gray-800">Page {pageNum}</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Position: {index + 1}</p>
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex items-center justify-center space-x-1">
                                    {/* Preview Button */}
                                    <button
                                      onClick={() => openMergePreviewModal(2, pageNum, thumbnailIndex)}
                                      className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                      title="Preview page"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Rotate Button */}
                                    <button
                                      onClick={() => rotateMergePdf2Page(index)}
                                      className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                      title="Rotate page 90°"
                                    >
                                      <RotateCw className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Move Up Button */}
                                    <button
                                      onClick={() => moveMergePageUp(2, index)}
                                      disabled={index === 0}
                                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Move up"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                      </svg>
                                    </button>
                                    
                                    {/* Move Down Button */}
                                    <button
                                      onClick={() => moveMergePageDown(2, index, mergePdf2.pageOrder.length)}
                                      disabled={index === mergePdf2.pageOrder.length - 1}
                                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Move down"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                    
                                    {/* Remove Button */}
                                    <button
                                      onClick={() => removeMergePage(2, index)}
                                      disabled={mergePdf2.pageOrder.length === 1}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                      title="Remove page"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Merged View - Single Column */}
                    {mergedDocPosition && (
                      <div className="bg-white border-2 border-green-500 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-green-800 flex items-center">
                            <CheckCircle className="h-5 w-5 mr-2" />
                            Merged Preview - Drag pages to rearrange
                          </h3>
                          <button
                            onClick={removeMergedDoc}
                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
                          >
                            Undo Merge
                          </button>
                        </div>

                        {/* Show combined pages with drag-and-drop */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {(() => {
                            const combinedPages = getMergedPageOrder();
                            const sourcePdfPages = mergedDocPosition.sourcePdf === 'pdf1' ? mergePdf1.pageOrder.length : mergePdf2.pageOrder.length;
                            
                            return combinedPages.map((item, index) => {
                              const pdf = item.pdf === 'pdf1' ? mergePdf1 : (item.pdf === 'pdf2' ? mergePdf2 : (item.pdf === 1 ? mergePdf1 : mergePdf2));
                              const thumbnailIndex = item.page - 1;
                              const isDragging = draggedMergedPageIndex === index;
                              
                              // Determine if this is from the merged PDF
                              const isFromMerged = !mergedCustomOrder && (
                                index >= mergedDocPosition.insertAt &&
                                index < mergedDocPosition.insertAt + sourcePdfPages
                              );

                              return (
                                <div
                                  key={index}
                                  className="relative"
                                >
                                  <div
                                    draggable
                                    onDragStart={(e) => handleMergedPageDragStart(e, index)}
                                    onDragOver={handleMergedPageDragOver}
                                    onDrop={(e) => handleMergedPageDrop(e, index)}
                                    onDragEnd={handleMergedPageDragEnd}
                                    className={`bg-white rounded-lg border-2 p-3 cursor-move transition-all ${
                                      isDragging 
                                        ? 'opacity-50 scale-95 border-blue-400' 
                                        : isFromMerged 
                                          ? 'border-yellow-400 bg-yellow-50 hover:border-yellow-500' 
                                          : 'border-gray-200 hover:border-blue-300'
                                    }`}
                                  >
                                    {/* Thumbnail */}
                                    {pdf.thumbnails[thumbnailIndex] && (
                                      <div className="mb-2">
                                        <img 
                                          src={pdf.thumbnails[thumbnailIndex]} 
                                          alt={`Page ${item.page}`}
                                          className="w-full h-auto object-contain border border-gray-200 rounded transition-transform"
                                          style={{ 
                                            transform: `rotate(${mergedPageRotations[index] || 0}deg)`,
                                            transition: 'transform 0.3s ease'
                                          }}
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Page Info */}
                                    <div className="text-center mb-2">
                                      <span className="text-xs font-medium text-gray-700">
                                        {(item.pdf === 'pdf1' || item.pdf === 1) ? 'PDF 1' : 'PDF 2'} - Page {item.page}
                                      </span>
                                      <p className="text-xs text-gray-500">Position: {index + 1}</p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-center space-x-1">
                                      {/* Preview Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openMergedPagePreview(item, thumbnailIndex);
                                        }}
                                        className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                        title="Preview page"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </button>
                                      
                                      {/* Rotate Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          rotateMergedPage(index);
                                        }}
                                        className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                        title="Rotate page 90°"
                                      >
                                        <RotateCw className="h-4 w-4" />
                                      </button>
                                      
                                      {/* Move Up Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveMergedPageUp(index);
                                        }}
                                        disabled={index === 0}
                                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        title="Move up"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                      </button>
                                      
                                      {/* Move Down Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveMergedPageDown(index);
                                        }}
                                        disabled={index === combinedPages.length - 1}
                                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        title="Move down"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      
                                      {/* Delete Button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeMergedPage(index);
                                        }}
                                        disabled={combinedPages.length === 1}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        title="Remove page"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'image' && (
                  <div className="space-y-6">
                    
                    {/* Upload Section with Generate PDF button */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            id="image-upload-input"
                            accept="image/*"
                            multiple
                            onChange={handleImageFilesSelect}
                            className="hidden"
                          />
                          <label
                            htmlFor="image-upload-input"
                            className="inline-flex items-center justify-center px-6 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors hover:opacity-90"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {imageFiles.length === 0 ? 'Upload Images' : 'Add More Images'}
                          </label>
                        </div>
                        
                        {imageFiles.length > 0 && (
                          <button
                            onClick={handleImageToPDF}
                            disabled={loading}
                            className="px-6 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 whitespace-nowrap"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            {loading ? (
                              <span className="flex items-center">
                                <Loader className="animate-spin h-4 w-4 mr-2" />
                                Converting...
                              </span>
                            ) : (
                              'Generate PDF'
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {imageFiles.length > 0 && (
                      <>
                        {/* Image Enhancement Option */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={enhanceImages}
                              onChange={(e) => setEnhanceImages(e.target.checked)}
                              className="rounded border-gray-300 text-theme-blue focus:ring-blue-500 focus:ring-2 w-4 h-4"
                            />
                            <span className="text-sm font-medium text-gray-800">Enhance image quality (contrast, sharpness, brightness)</span>
                          </label>
                        </div>

                        {/* Image Thumbnails Grid */}
                        <div className="border border-gray-300 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-800 mb-3">
                            Images ({imageFiles.length}) - Arrange In Desired Order
                          </h3>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {imageFiles.map((file, index) => (
                              <div
                                key={index}
                                className="bg-white rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-blue-300 hover:shadow-md"
                              >
                                {/* Thumbnail */}
                                {imageThumbnails[index] && (
                                  <div className="mb-3 relative">
                                    <img 
                                      src={imageThumbnails[index]} 
                                      alt={file.name}
                                      className="w-full h-auto object-contain border border-gray-200 rounded cursor-pointer"
                                      onClick={() => setImagePreviewModal({ show: true, index: index, url: imageThumbnails[index] })}
                                    />
                                    {/* File Type Badge - Bottom Right */}
                                    <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-bold text-blue-900 bg-white bg-opacity-90 rounded shadow-sm">
                                      .{file.name.split('.').pop().toLowerCase()}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Image Info */}
                                <div className="text-center mb-2">
                                  <span className="text-sm font-semibold text-gray-800">Image {index + 1}</span>
                                  <p className="text-xs text-gray-500 mt-0.5 break-words line-clamp-2">{file.name}</p>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => setImagePreviewModal({ show: true, index: index, url: imageThumbnails[index] })}
                                    className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Preview image"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => moveImageUp(index)}
                                    disabled={index === 0}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                    title="Move up"
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => moveImageDown(index)}
                                    disabled={index === imageFiles.length - 1}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                    title="Move down"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => removeImage(index)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                    title="Remove"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Add/Remove Page Numbers Tab */}
                {activeTab === 'pagenumbers' && (
                  <div className="space-y-6">
                    
                    {/* Upload Section with Action Buttons */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        {/* Custom Upload Button */}
                        <div className="flex-1 flex items-center gap-3">
                          <input
                            type="file"
                            id="pagenum-upload-input"
                            accept="application/pdf"
                            onChange={handlePageNumFileSelect}
                            disabled={loadingThumbnails}
                            className="hidden"
                          />
                          <label
                            htmlFor="pagenum-upload-input"
                            className={`inline-flex items-center justify-center px-6 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors ${
                              loadingThumbnails 
                                ? 'opacity-50 cursor-not-allowed' 
                                : 'hover:opacity-90'
                            }`}
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF
                          </label>
                          
                          {/* Browse Session Button */}
                          {(sessionGeneratedPDFs.length > 0 || sessionUploadedPDFs.length > 0) && (
                            <button
                              onClick={() => setSessionBrowserModal({ show: true, targetTab: 'pagenumbers' })}
                              disabled={loadingThumbnails}
                              className="inline-flex items-center justify-center px-4 py-2 bg-white border-2 border-blue-400 text-theme-blue rounded-lg font-medium text-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
                            >
                              <FolderOpen className="h-4 w-4 mr-2" />
                              Browse Session
                            </button>
                          )}
                        </div>
                        
                        {pageNumFile && !loadingThumbnails && (
                          <div className="flex gap-2">
                            {/* Reset Button */}
                            <button
                              onClick={resetPageNumbers}
                              className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium text-sm hover:bg-gray-700 transition-colors whitespace-nowrap"
                            >
                              Reset
                            </button>
                            
                            {/* Generate PDF Button */}
                            <button
                              onClick={handleProcessPageNumbers}
                              disabled={loading || (!pageNumAddNumbers && !pageNumRemoveNumbers)}
                              className="px-6 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              title={(!pageNumAddNumbers && !pageNumRemoveNumbers) ? 'Select at least one operation' : 'Process page numbers'}
                            >
                              {loading ? (
                                <span className="flex items-center">
                                  <Loader className="animate-spin h-4 w-4 mr-2" />
                                  Processing...
                                </span>
                              ) : (
                                'Generate PDF'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Page Number Options - Show only when file is uploaded */}
                    {pageNumFile && !loadingThumbnails && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4">Page Number Options</h3>
                        
                        <div className="space-y-4">
                          {/* Remove Page Numbers Section */}
                          <div className="border-l-4 border-red-400 bg-red-50 rounded-r-lg p-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pageNumRemoveNumbers}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPageNumAddNumbers(false); // Uncheck Add when Remove is checked
                                  }
                                  setPageNumRemoveNumbers(e.target.checked);
                                }}
                                className="rounded border-gray-300 text-red-600 focus:ring-red-500 focus:ring-2 w-4 h-4"
                              />
                              <span className="text-sm font-medium text-gray-800">Remove Existing Page Numbers</span>
                            </label>
                            
                            {pageNumRemoveNumbers && (
                              <div className="mt-3 ml-6 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Header/Footer Selection */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Select Area:</label>
                                    <select
                                      value={pageNumArea}
                                      onChange={(e) => setPageNumArea(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    >
                                      <option value="footer">Footer (Bottom)</option>
                                      <option value="header">Header (Top)</option>
                                    </select>
                                  </div>
                                  
                                  {/* Location Selection */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Page Number Location:</label>
                                    <select
                                      value={pageNumLocation}
                                      onChange={(e) => setPageNumLocation(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    >
                                      <option value="left">{pageNumArea === 'header' ? 'Header' : 'Footer'} Left</option>
                                      <option value="center-left">{pageNumArea === 'header' ? 'Header' : 'Footer'} Center - More Left</option>
                                      <option value="center">{pageNumArea === 'header' ? 'Header' : 'Footer'} Center</option>
                                      <option value="center-right">{pageNumArea === 'header' ? 'Header' : 'Footer'} Center - More Right</option>
                                      <option value="right">{pageNumArea === 'header' ? 'Header' : 'Footer'} Right</option>
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Format Selection for Remove */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Number Format:</label>
                                    <select
                                      value={pageNumRemoveFormat}
                                      onChange={(e) => setPageNumRemoveFormat(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    >
                                      <option value="auto">Auto Detect (Recommended)</option>
                                      <option value="number">1, 2, 3...</option>
                                      <option value="page-number">Page 1, Page 2...</option>
                                      <option value="page-of-total">Page 1 of 10</option>
                                      <option value="fraction">1/10, 2/10...</option>
                                      <option value="roman-lower">i, ii, iii, iv...</option>
                                      <option value="roman-upper">I, II, III, IV...</option>
                                    </select>
                                  </div>
                                  
                                  {/* Font Weight Selection for Remove */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Font Weight:</label>
                                    <select
                                      value={pageNumRemoveFontWeight}
                                      onChange={(e) => setPageNumRemoveFontWeight(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    >
                                      <option value="auto">Auto Detect (Recommended)</option>
                                      <option value="normal">Normal</option>
                                      <option value="bold">Bold</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Add Page Numbers Section */}
                          <div className="border-l-4 border-blue-400 bg-blue-50 rounded-r-lg p-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pageNumAddNumbers}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPageNumRemoveNumbers(false); // Uncheck Remove when Add is checked
                                  }
                                  setPageNumAddNumbers(e.target.checked);
                                }}
                                className="rounded border-gray-300 text-theme-blue focus:ring-blue-500 focus:ring-2 w-4 h-4"
                              />
                              <span className="text-sm font-medium text-gray-800">Add New Page Numbers</span>
                            </label>
                            
                            {pageNumAddNumbers && (
                              <div className="mt-3 ml-6 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Area Selection for Add */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Add to Area:</label>
                                    <select
                                      value={pageNumAddArea}
                                      onChange={(e) => setPageNumAddArea(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                                    >
                                      <option value="footer">Footer (Bottom)</option>
                                      <option value="header">Header (Top)</option>
                                    </select>
                                  </div>
                                  
                                  {/* Location Selection for Add */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Position:</label>
                                    <select
                                      value={pageNumAddLocation}
                                      onChange={(e) => setPageNumAddLocation(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                                    >
                                      <option value="left">{pageNumAddArea === 'header' ? 'Header' : 'Footer'} Left</option>
                                      <option value="center-left">{pageNumAddArea === 'header' ? 'Header' : 'Footer'} Center - More Left</option>
                                      <option value="center">{pageNumAddArea === 'header' ? 'Header' : 'Footer'} Center</option>
                                      <option value="center-right">{pageNumAddArea === 'header' ? 'Header' : 'Footer'} Center - More Right</option>
                                      <option value="right">{pageNumAddArea === 'header' ? 'Header' : 'Footer'} Right</option>
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Format Selection */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Number Format:</label>
                                    <select
                                      value={pageNumFormat}
                                      onChange={(e) => setPageNumFormat(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                                    >
                                      <option value="number">1, 2, 3...</option>
                                      <option value="page-number">Page 1, Page 2...</option>
                                      <option value="page-of-total">Page 1 of 10</option>
                                      <option value="fraction">1/10, 2/10...</option>
                                      <option value="roman-lower">i, ii, iii, iv...</option>
                                      <option value="roman-upper">I, II, III, IV...</option>
                                    </select>
                                  </div>
                                  
                                  {/* Font Weight Selection */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Font Weight:</label>
                                    <select
                                      value={pageNumFontWeight}
                                      onChange={(e) => setPageNumFontWeight(e.target.value)}
                                      className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                                    >
                                      <option value="normal">Normal</option>
                                      <option value="bold">Bold</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {loadingThumbnails && (
                      <div className="flex items-center justify-center py-8">
                        <Loader className="animate-spin h-8 w-8 mr-2" style={{ color: 'var(--theme-primary)' }} />
                        <span className="text-sm text-gray-600">Loading PDF and generating thumbnails...</span>
                      </div>
                    )}

                    {/* PDF Thumbnails Preview */}
                    {pageNumFile && pageNumPages.length > 0 && !loadingThumbnails && (
                      <div className="border border-gray-300 rounded-lg p-4">
                        {/* Filename Header */}
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-800">
                            {pageNumFile.name.split('.').pop().toUpperCase()} - {pageNumFile.name} ({pageNumPages.length} {pageNumPages.length === 1 ? 'page' : 'pages'})
                          </h3>
                          
                          {/* Selection Label and Select All Buttons - Only show when Add is enabled */}
                          {pageNumAddNumbers && (
                            <div className="flex items-center gap-3">
                              {pageNumSelectedPages.length > 0 && (
                                <span className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                                  {pageNumSelectedPages.length} Selected For Numbering
                                </span>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={selectAllPageNums}
                                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                >
                                  Select All
                                </button>
                                <button
                                  onClick={deselectAllPageNums}
                                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                >
                                  Deselect All
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Grid Layout - 5 columns matching rearrange */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                          {pageNumPages.map((pageNum, index) => {
                            const isSelected = pageNumSelectedPages.includes(pageNum);
                            return (
                              <div
                                key={index}
                                className={`bg-white rounded-lg border-2 p-3 transition-all ${
                                  pageNumAddNumbers && isSelected 
                                    ? 'border-blue-400 shadow-md' 
                                    : pageNumAddNumbers && !isSelected
                                    ? 'border-gray-200 opacity-60'
                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                              >
                                {/* Checkbox - Only show when Add is enabled */}
                                {pageNumAddNumbers && (
                                  <div className="flex justify-end mb-1">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => togglePageNumSelection(pageNum)}
                                      className="rounded border-gray-300 text-theme-blue focus:ring-blue-500 focus:ring-2 w-4 h-4 cursor-pointer"
                                    />
                                  </div>
                                )}
                                
                                {/* Thumbnail */}
                                {pageNumThumbnails[index] && (
                                  <div className="mb-3">
                                    <img 
                                      src={pageNumThumbnails[index]} 
                                      alt={`Page ${pageNum}`}
                                      className="w-full h-auto object-contain border border-gray-200 rounded cursor-pointer"
                                      onClick={() => setPreviewModal({ show: true, pageNum: pageNum, thumbnail: pageNumThumbnails[index], loading: false })}
                                    />
                                  </div>
                                )}
                                
                                {/* Page Info */}
                                <div className="text-center mb-2">
                                  <span className="text-sm font-semibold text-gray-800">Page {pageNum}</span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {pageNumAddNumbers 
                                      ? (isSelected ? 'Will add numbers' : 'Skip numbering')
                                      : 'Preview Only'
                                    }
                                  </p>
                                </div>
                                
                                {/* Preview Button */}
                                <div className="flex items-center justify-center">
                                  <button
                                    onClick={() => setPreviewModal({ show: true, pageNum: pageNum, thumbnail: pageNumThumbnails[index], loading: false })}
                                    className="p-1.5 text-theme-blue hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Preview page"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* DOC to PDF Tab */}
                {activeTab === 'doc' && (
                  <div className="space-y-6">
                    
                    {/* Upload Section with Generate PDF button */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            id="doc-upload-input"
                            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={handleDocFileSelect}
                            multiple
                            className="hidden"
                          />
                          <label
                            htmlFor="doc-upload-input"
                            className="inline-flex items-center justify-center px-6 py-2 text-white rounded-lg font-medium text-sm cursor-pointer transition-colors hover:opacity-90"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {docFiles.length === 0 ? 'Upload DOC/DOCX' : 'Add More Documents'}
                          </label>
                        </div>
                        
                        {docFiles.length > 0 && (
                          <button
                            onClick={handleDocToPDF}
                            disabled={loading}
                            className="px-6 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-50 whitespace-nowrap"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                          >
                            {loading ? (
                              <span className="flex items-center">
                                <Loader className="animate-spin h-4 w-4 mr-2" />
                                Converting...
                              </span>
                            ) : (
                              'Generate PDF'
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Document Cards Grid */}
                    {docFiles.length > 0 && (
                      <div className="border border-gray-300 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3">
                          Documents ({docFiles.length}) - Arrange In Desired Order
                        </h3>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {docFiles.map((file, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-blue-300 hover:shadow-md"
                            >
                              {/* Document Icon */}
                              <div className="mb-3 flex items-center justify-center h-32 bg-blue-50 rounded border border-blue-200 relative">
                                <FileText className="h-16 w-16 text-theme-blue" />
                                {/* File Type Badge - Bottom Right */}
                                <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-bold text-blue-900 bg-white bg-opacity-90 rounded shadow-sm">
                                  .{file.name.split('.').pop().toLowerCase()}
                                </span>
                              </div>
                              
                              {/* Document Info */}
                              <div className="text-center mb-2">
                                <span className="text-sm font-semibold text-gray-800">Doc {index + 1}</span>
                                <p className="text-xs text-gray-500 mt-0.5 break-words line-clamp-2" title={file.name}>{file.name}</p>
                                <p className="text-xs text-gray-400 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleDocPreview(file)}
                                  className="p-1.5 text-theme-blue hover:bg-blue-50 rounded"
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => moveDocUp(index)}
                                  disabled={index === 0}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move up"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => moveDocDown(index)}
                                  disabled={index === docFiles.length - 1}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move down"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => removeDoc(index)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Remove"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Session Tab */}
                {activeTab === 'session' && (
                  <div className="space-y-6">
                    {/* Generated PDFs Section */}
                    <div className="bg-white rounded-lg p-6 border border-gray-300">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                        <Download className="h-4 w-4 mr-2 text-green-600" />
                        Generated PDFs ({sessionGeneratedPDFs.length})
                      </h3>
                      {sessionGeneratedPDFs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No generated PDFs yet. Generate PDFs using the tools above.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {sessionGeneratedPDFs.map((pdf, index) => (
                            <div
                              key={`${pdf.filename}-${pdf.timestamp}`}
                              className="bg-gray-50 rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-green-400 hover:shadow-md"
                            >
                              {/* Thumbnail */}
                              <div className="mb-3 relative cursor-pointer" onClick={() => setSessionPreviewModal({ show: true, pdf })}>
                                <img 
                                  src={pdf.thumbnail} 
                                  alt={pdf.filename}
                                  className="w-full h-auto max-h-40 object-contain border border-gray-300 rounded"
                                />
                                <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-bold text-blue-900 bg-white bg-opacity-90 rounded shadow-sm">
                                  .pdf
                                </span>
                              </div>
                              
                              {/* File Info */}
                              <div className="text-center mb-2">
                                <p className="text-xs font-semibold text-gray-800 break-words line-clamp-2" title={pdf.filename}>{pdf.filename}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(pdf.timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => setSessionPreviewModal({ show: true, pdf })}
                                  className="p-1.5 text-theme-blue hover:bg-blue-50 rounded"
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = `data:application/pdf;base64,${pdf.pdfBase64}`;
                                    link.download = pdf.filename;
                                    link.click();
                                  }}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSessionGeneratedPDFs(prev => prev.filter((_, i) => i !== index));
                                    showMessage('success', 'PDF removed from session');
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Remove"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Uploaded PDFs Section */}
                    <div className="bg-white rounded-lg p-6 border border-gray-300">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                        <Upload className="h-4 w-4 mr-2 text-theme-blue" />
                        Uploaded Documents ({sessionUploadedPDFs.length})
                      </h3>
                      {sessionUploadedPDFs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Upload className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No uploaded documents yet. Upload PDFs using the tools above.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {sessionUploadedPDFs.map((pdf, index) => (
                            <div
                              key={`${pdf.filename}-${pdf.timestamp}`}
                              className="bg-gray-50 rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-blue-400 hover:shadow-md"
                            >
                              {/* Thumbnail */}
                              <div className="mb-3 relative cursor-pointer" onClick={() => setSessionPreviewModal({ show: true, pdf })}>
                                <img 
                                  src={pdf.thumbnail} 
                                  alt={pdf.filename}
                                  className="w-full h-auto max-h-40 object-contain border border-gray-300 rounded"
                                />
                                <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-bold text-blue-900 bg-white bg-opacity-90 rounded shadow-sm">
                                  .pdf
                                </span>
                              </div>
                              
                              {/* File Info */}
                              <div className="text-center mb-2">
                                <p className="text-xs font-semibold text-gray-800 break-words line-clamp-2" title={pdf.filename}>{pdf.filename}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(pdf.timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => setSessionPreviewModal({ show: true, pdf })}
                                  className="p-1.5 text-theme-blue hover:bg-blue-50 rounded"
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(pdf.file);
                                    link.download = pdf.filename;
                                    link.click();
                                  }}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSessionUploadedPDFs(prev => prev.filter((_, i) => i !== index));
                                    showMessage('success', 'PDF removed from session');
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Remove"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal - Rendered via Portal (Available for all tabs) */}
      {downloadModal.show && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setDownloadModal({ show: false, result: null })}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  PDF Generated Successfully!
                </h3>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-2">
                {activeTab === 'merge' 
                  ? 'Your PDFs have been successfully merged.' 
                  : 'Your PDF has been rearranged with the new page order.'}
              </p>
              {downloadModal.result && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Pages:</span> {downloadModal.result.total_pages}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">Filename:</span> {downloadModal.result.filename}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Click the download button below to save your file.
              </p>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setDownloadModal({ show: false, result: null })}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  previewPDF(downloadModal.result);
                  // Keep modal open so user can still download after preview
                }}
                className="px-4 py-2 bg-white text-theme-blue border-2 border-theme-blue rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm flex items-center"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview PDF
              </button>
              <button
                onClick={() => {
                  const filename = activeTab === 'merge' ? 'merged.pdf' : activeTab === 'pagenumbers' ? 'processed.pdf' : 'rearranged.pdf';
                  downloadPDF(downloadModal.result, filename);
                  setDownloadModal({ show: false, result: null });
                }}
                className="px-6 py-2 text-white rounded-lg font-medium text-sm flex items-center"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Preview Modal - Rendered via Portal (Available for all tabs) */}
      {previewModal.show && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setPreviewModal({ show: false, pageNum: null, thumbnail: null, loading: false })}
        >
          <div 
            className="relative max-w-6xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Content */}
            <div className="flex justify-center items-center relative">
              {previewModal.loading ? (
                <div className="flex flex-col items-center bg-white rounded-lg p-12">
                  <Loader className="animate-spin h-12 w-12 mb-4" style={{ color: 'var(--theme-primary)' }} />
                  <p className="text-sm text-gray-600">Loading high-resolution preview...</p>
                </div>
              ) : (
                <div className="relative inline-block">
                  <img 
                    src={previewModal.thumbnail} 
                    alt={`Page ${previewModal.pageNum} Preview`}
                    className="max-w-full h-auto rounded-lg shadow-2xl"
                    style={{ maxHeight: '90vh' }}
                  />
                  
                  {/* Close Button - On top right corner of the PDF image */}
                  <button
                    onClick={() => setPreviewModal({ show: false, pageNum: null, thumbnail: null, loading: false })}
                    className="absolute top-3 right-3 bg-gray-200 bg-opacity-80 hover:bg-opacity-100 rounded-md p-2 shadow-lg transition-all"
                    title="Close"
                  >
                    <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerModal.show && (
        <PDFViewer
          fileUrl={pdfViewerModal.url}
          fileName={pdfViewerModal.filename}
          onClose={() => {
            // Revoke the object URL to free memory
            if (pdfViewerModal.url) {
              window.URL.revokeObjectURL(pdfViewerModal.url);
            }
            setPdfViewerModal({ show: false, url: null, filename: null });
          }}
          token={token}
        />
      )}

      {/* Image Preview Modal */}
      {imagePreviewModal.show && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setImagePreviewModal({ show: false, index: null, url: null })}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Image Preview - Image {imagePreviewModal.index !== null ? imagePreviewModal.index + 1 : ''}
                </h3>
                <button
                  onClick={() => setImagePreviewModal({ show: false, index: null, url: null })}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-140px)] bg-gray-100 flex items-center justify-center">
              <img 
                src={imagePreviewModal.url} 
                alt={`Image ${imagePreviewModal.index + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setImagePreviewModal({ show: false, index: null, url: null })}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Doc Preview Modal - Using OfficeViewer */}
      {docPreviewModal.show && (
        <OfficeViewer
          fileUrl={docPreviewModal.fileUrl}
          fileName={docPreviewModal.fileName}
          onClose={() => setDocPreviewModal({ show: false, fileUrl: null, fileName: null })}
          token={token}
        />
      )}

      {/* Session PDF Preview Modal */}
      {sessionPreviewModal.show && sessionPreviewModal.pdf && (
        <PDFViewer
          fileUrl={sessionPreviewModal.pdf.type === 'uploaded' 
            ? URL.createObjectURL(sessionPreviewModal.pdf.file)
            : `data:application/pdf;base64,${sessionPreviewModal.pdf.pdfBase64}`}
          fileName={sessionPreviewModal.pdf.filename}
          onClose={() => setSessionPreviewModal({ show: false, pdf: null })}
          token={token}
        />
      )}

      {/* Session Browser Modal */}
      {sessionBrowserModal.show && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setSessionBrowserModal({ show: false, targetTab: null })}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <FolderOpen className="h-5 w-5 text-theme-blue mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Browse Session Documents</h3>
              </div>
              <button
                onClick={() => setSessionBrowserModal({ show: false, targetTab: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Generated PDFs Section */}
              {sessionGeneratedPDFs.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Download className="h-4 w-4 mr-2 text-green-600" />
                    Generated PDFs ({sessionGeneratedPDFs.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {sessionGeneratedPDFs.map((pdf, index) => (
                      <div
                        key={`gen-${index}`}
                        onClick={() => {
                          handleSelectFromSession(pdf, sessionBrowserModal.targetTab);
                          setSessionBrowserModal({ show: false, targetTab: null });
                        }}
                        className="bg-gray-50 rounded-lg border-2 border-gray-200 p-2 cursor-pointer transition-all hover:border-green-400 hover:shadow-md"
                      >
                        <img 
                          src={pdf.thumbnail} 
                          alt={pdf.filename}
                          className="w-full h-auto max-h-32 object-contain border border-gray-300 rounded mb-2"
                        />
                        <p className="text-xs font-semibold text-gray-800 break-words line-clamp-2" title={pdf.filename}>
                          {pdf.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Uploaded PDFs Section */}
              {sessionUploadedPDFs.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Upload className="h-4 w-4 mr-2 text-theme-blue" />
                    Uploaded PDFs ({sessionUploadedPDFs.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {sessionUploadedPDFs.map((pdf, index) => (
                      <div
                        key={`upl-${index}`}
                        onClick={() => {
                          handleSelectFromSession(pdf, sessionBrowserModal.targetTab);
                          setSessionBrowserModal({ show: false, targetTab: null });
                        }}
                        className="bg-gray-50 rounded-lg border-2 border-gray-200 p-2 cursor-pointer transition-all hover:border-blue-400 hover:shadow-md"
                      >
                        <img 
                          src={pdf.thumbnail} 
                          alt={pdf.filename}
                          className="w-full h-auto max-h-32 object-contain border border-gray-300 rounded mb-2"
                        />
                        <p className="text-xs font-semibold text-gray-800 break-words line-clamp-2" title={pdf.filename}>
                          {pdf.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSessionBrowserModal({ show: false, targetTab: null })}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Session Extend Modal */}
      {sessionExtendModal && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
          style={{ zIndex: 999999 }}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-pulse">
            <div className="text-center">
              <div className="mb-4">
                <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Session Expired
              </h3>
              <p className="text-gray-600 mb-6">
                Your 10-minute session has ended. Would you like to extend it by another 10 minutes?
              </p>
              
              <div className="text-sm text-gray-500 mb-6">
                ⚠️ Page will auto-refresh in 30 seconds if no action is taken
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={clearSession}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  End Session
                </button>
                <button
                  onClick={extendSession}
                  className="flex-1 px-4 py-3 text-white rounded-lg hover:opacity-90 transition-colors font-medium"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  Extend 10 Minutes
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PDFTool;
